import { cn } from '@/lib/utils';
import { imageService } from '@/services/image';
import { useStoreCard } from './useStoreCard';
import { StoreCardBadges, HorizontalFavoriteButton } from './StoreCardBadges';
import { StoreCardInfo } from './StoreCardInfo';
import type { StoreCardProps } from './store-card.types';

const PLACEHOLDER_IMAGE = '/images/products/background_placeholder.png';

export function StoreCard(props: StoreCardProps) {
  const {
    name,
    imageUrl,
    onCardClick,
    className = '',
    layout = 'vertical'
  } = props;

  const {
    favoriteState,
    priceInfo,
    pickupTimeText,
    orderAvailable,
    bestMenuText,
    timeLabel,
    sizeStyles,
    deadlineStockBadgeText,
    handleFavoriteToggle
  } = useStoreCard(props);

  const handleFavoriteClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    handleFavoriteToggle();
  };

  const handleCardClick = () => {
    onCardClick?.();
  };

  const resolvedImageUrl = imageUrl && !imageUrl.startsWith('/home/') ? imageUrl : PLACEHOLDER_IMAGE;

  if (layout === 'horizontal') {
    return (
      <div 
        className={cn("w-full cursor-pointer mb-4", className)}
        onClick={handleCardClick}
        style={{
          opacity: orderAvailable ? 1 : 0.6,
          filter: orderAvailable ? 'none' : 'grayscale(50%)'
        }}
      >
        <div className="flex bg-white border-2 border-[#DEDEDE] rounded-xl overflow-hidden">
          {/* 이미지 영역 */}
          <div 
            className="relative flex-shrink-0 overflow-hidden"
            style={{
              width: sizeStyles.imageHeight,
              height: sizeStyles.imageHeight,
              borderRadius: '12px 0 0 12px'
            }}
          >
            <img 
              src={resolvedImageUrl || imageService.getRestaurantImageByName(name)}
              alt={name}
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                objectPosition: 'top center',
                display: 'block'
              }}
            />
            
            <StoreCardBadges
              isNew={props.isNew}
              orderAvailable={orderAvailable}
              onFavoriteClick={handleFavoriteClick}
              favoriteState={favoriteState}
              layout="horizontal"
              deadlineStockBadgeText={deadlineStockBadgeText}
              orderType={props.selectedOrderType}
            />

            <HorizontalFavoriteButton
              onFavoriteClick={handleFavoriteClick}
              favoriteState={favoriteState}
            />
          </div>

          <StoreCardInfo
            name={name}
            bestMenuText={bestMenuText}
            timeLabel={timeLabel}
            pickupTimeText={pickupTimeText}
            distance={props.distance}
            priceInfo={priceInfo}
            sizeStyles={sizeStyles}
            layout={layout}
          />
        </div>
      </div>
    );
  }

  return (
    <div 
      className={cn("cursor-pointer", className)}
      onClick={handleCardClick}
      style={{
        width: '100%',
        maxWidth: '360px',
        border: '2px solid #DEDEDE',
        borderRadius: '12px',
        overflow: 'hidden',
        backgroundColor: 'white',
        margin: '0',
        padding: '0',
        boxSizing: 'border-box',
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
        opacity: orderAvailable ? 1 : 0.6,
        filter: orderAvailable ? 'none' : 'grayscale(50%)'
      }}
    >
      {/* 상단 이미지 영역 */}
      <div 
        className="relative w-full overflow-hidden flex-shrink-0"
        style={{
          aspectRatio: '360 / 100',
          borderRadius: '10px 10px 0 0'
        }}
      >
        <img 
          src={resolvedImageUrl || imageService.getRestaurantImageUrl(name)}
          alt={name}
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            objectPosition: 'top center',
            display: 'block'
          }}
        />
        
        <StoreCardBadges
          isNew={props.isNew}
          orderAvailable={orderAvailable}
          onFavoriteClick={handleFavoriteClick}
          favoriteState={favoriteState}
          layout="vertical"
          deadlineStockBadgeText={deadlineStockBadgeText}
          orderType={props.selectedOrderType}
        />

        {/* 매장명 (하단 좌측 오버레이) */}
        <div className="absolute max-w-full" style={{ bottom: '16px', left: '12px', right: '12px' }}>
          <h3 className="text-[24px] font-bold text-left truncate" style={{ color: '#FFFFFF', margin: '0', lineHeight: '1.1' }}>
            {name}
          </h3>
        </div>
      </div>

      <StoreCardInfo
        name={name}
        bestMenuText={bestMenuText}
        timeLabel={timeLabel}
        pickupTimeText={pickupTimeText}
        distance={props.distance}
        priceInfo={priceInfo}
        sizeStyles={sizeStyles}
        layout={layout}
      />
    </div>
  );
}
